/**
 * reportPeriod.ts — date-range model for the shared report/compensation date picker.
 *
 * Ported from the custom picker's `report-data.js` (`window.RD`) but reimplemented
 * on top of date-fns (already a project dependency) instead of hand-rolled math.
 * Time boundaries are clamped with LOCAL helpers (end at 23:59:59.000) to match the
 * original mock exactly — date-fns' `endOfDay` would land on 23:59:59.999.
 */
import {
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  subMonths,
  subQuarters,
  subYears,
  format
} from 'date-fns';
import { getBusinessDateString } from '@/utils/dateHelpers';

export type PeriodMode = 'mtd' | 'qtd' | 'ytd' | 'lastMonth' | 'lastQuarter' | 'lastYear' | 'custom';
export type PresetId = Exclude<PeriodMode, 'custom'>;

export interface ReportPeriod {
  mode: PeriodMode;
  label: string;
  from: Date;
  to: Date;
}

export interface PresetDef {
  id: PresetId;
  short: string;
  label: string;
}

export const PRESETS: ReadonlyArray<PresetDef> = [
  { id: 'mtd', short: 'MTD', label: 'Month to date' },
  { id: 'qtd', short: 'QTD', label: 'Quarter to date' },
  { id: 'ytd', short: 'YTD', label: 'Year to date' },
  { id: 'lastMonth', short: 'Last month', label: 'Last month' },
  { id: 'lastQuarter', short: 'Last quarter', label: 'Last quarter' },
  { id: 'lastYear', short: 'Last year', label: 'Last year' }
];

// Local clamps — start of day at 00:00:00.000, end of day at 23:59:59.000 (matches the mock).
const clampStart = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const clampEnd = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 0);

const mk = (mode: PeriodMode, label: string, from: Date, to: Date): ReportPeriod => ({
  mode,
  label,
  from: clampStart(from),
  to: clampEnd(to)
});

/** Pacific business date represented as a browser-local calendar Date. */
export function businessTodayAsLocalDate(now: Date = new Date()): Date {
  const [year, month, day] = getBusinessDateString(now).split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

/** Build a preset range relative to `today` (defaults to now). Mirrors the mock's RD.preset. */
export function makePreset(mode: PresetId, today: Date = new Date()): ReportPeriod {
  switch (mode) {
    case 'mtd':
      return mk('mtd', 'Month to date', startOfMonth(today), today);
    case 'qtd':
      return mk('qtd', 'Quarter to date', startOfQuarter(today), today);
    case 'ytd':
      return mk('ytd', 'Year to date', startOfYear(today), today);
    case 'lastMonth': {
      const m = subMonths(today, 1);
      return mk('lastMonth', 'Last month', startOfMonth(m), endOfMonth(m));
    }
    case 'lastQuarter': {
      const q = subQuarters(today, 1);
      return mk('lastQuarter', 'Last quarter', startOfQuarter(q), endOfQuarter(q));
    }
    case 'lastYear': {
      const y = subYears(today, 1);
      return mk('lastYear', 'Last year', startOfYear(y), endOfYear(y));
    }
    default:
      return mk('ytd', 'Year to date', startOfYear(today), today);
  }
}

/** Build a custom range from two endpoints (swaps if reversed). Mirrors the mock's RD.custom. */
export function makeCustom(from: Date, to: Date): ReportPeriod {
  let start = from;
  let end = to;
  if (start > end) {
    const tmp = start;
    start = end;
    end = tmp;
  }
  return mk('custom', 'Custom range', start, end);
}

export function fmtDate(d: Date | null | undefined): string {
  return d ? format(d, 'MMM dd, yyyy') : '—';
}

export function fmtRange(p: { from?: Date | null; to?: Date | null } | null | undefined): string {
  if (!p || !p.from || !p.to) {
    return 'Select dates';
  }
  return `${fmtDate(p.from)} – ${fmtDate(p.to)}`;
}
